import { gql } from '@apollo/client/core'
import slugify from 'slugify'
import {
  CreatePenNameDto,
  EntityDeletionSummaryDto,
  PenNameDto,
  PenNameGetByIdArgs,
  PenNamePagedResultDto,
  UpdatePenNameDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `PenNameService`.
 *
 * `getUrl` is the only member that talks to nothing: it builds the public pen-name url from the id and
 * a slugified name. It stays here because every consumer already holds the service.
 */

const crud = useBaseCrud<PenNameDto>('penName')

const penNameFragments = {
  penName: gql`
    fragment PenNameFragment on PenNameDto {
      id,
      name,
      firstName,
      lastName,
      bio,
      image,
      facebookLink,
      bookbubLink,
      twitterLink,
      instagramLink,
      websiteLink,
      newsletterLink,
      tiktokLink,
      amazonLink,
      goodreadsLink,
      isDraft,
      followerCount,
      averageRating,
      reviewCount,
      followers {
        id
      }
    }
  `
}

const get = (params?: any): Promise<PenNamePagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetPenNames (
        $genericSearchTerm: String,
        $genericSearch: Boolean,
        $skip: Int,
        $take: Int,
        $sortBy: String,
        $descending: Boolean,
        $following: Boolean
        $forUserId: Int
      ) {
        penNames (
          genericSearchTerm: $genericSearchTerm,
          genericSearch: $genericSearch,
          skip: $skip,
          take: $take,
          sortBy: $sortBy,
          descending: $descending,
          following: $following
          forUserId: $forUserId
        ) {
          totalRows,
          items {
            ...PenNameFragment
          }
        }
      }
      ${penNameFragments.penName}
    `
  })
}

/**
 * `args` is everything but the id, which arrives as the first parameter and is merged in below. The
 * class typed this `PenNameGetByIdArgs`, whose `id` is required — a signature no caller could satisfy.
 * It went unnoticed because `PenNameServiceInterface` inherited `getById (id?: number, args?: any)`
 * from `BaseCrudServiceInterface`, and every consumer held the interface.
 */
const getById = (id?: number, args?: Omit<PenNameGetByIdArgs, 'id'>): Promise<PenNameDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id,
      ...args
    },
    query: gql`
      query GetPenName (
        $id: Int!,
        $getAverageRating: Boolean,
        $genericSearch: Boolean
      ) {
        penName (id: $id, getAverageRating: $getAverageRating, genericSearch: $genericSearch) {
          ...PenNameFragment
        }
      }
      ${penNameFragments.penName}
    `
  })
}

const create = (dto: CreatePenNameDto): Promise<PenNameDto> => {
  return useApollo().mutate({
    variables: {
      name: dto.name,
      bio: dto.bio,
      image: dto.image,
      facebookLink: dto.facebookLink,
      bookbubLink: dto.bookbubLink,
      twitterLink: dto.twitterLink,
      instagramLink: dto.instagramLink,
      websiteLink: dto.websiteLink,
      newsletterLink: dto.newsletterLink,
      tiktokLink: dto.tiktokLink,
      amazonLink: dto.amazonLink,
      goodreadsLink: dto.goodreadsLink
    },
    mutation: gql`
      mutation CreatePenName (
        $name: String!,
        $bio: String,
        $image: String,
        $facebookLink: String,
        $bookbubLink: String,
        $twitterLink: String,
        $instagramLink: String,
        $websiteLink: String,
        $newsletterLink: String,
        $tiktokLink: String,
        $amazonLink: String,
        $goodreadsLink: String
      ) {
        createPenName (
          name: $name,
          bio: $bio,
          image: $image,
          facebookLink: $facebookLink,
          bookbubLink: $bookbubLink,
          twitterLink: $twitterLink,
          instagramLink: $instagramLink,
          websiteLink: $websiteLink,
          newsletterLink: $newsletterLink,
          tiktokLink: $tiktokLink,
          amazonLink: $amazonLink,
          goodreadsLink: $goodreadsLink
        ) {
          ...PenNameFragment
        }
      }
      ${penNameFragments.penName}
    `
  })
}

const update = (dto: UpdatePenNameDto): Promise<PenNameDto> => {
  return useApollo().mutate({
    variables: {
      id: dto.id,
      name: dto.name,
      bio: dto.bio,
      image: dto.image,
      facebookLink: dto.facebookLink,
      bookbubLink: dto.bookbubLink,
      twitterLink: dto.twitterLink,
      instagramLink: dto.instagramLink,
      websiteLink: dto.websiteLink,
      newsletterLink: dto.newsletterLink,
      tiktokLink: dto.tiktokLink,
      amazonLink: dto.amazonLink,
      goodreadsLink: dto.goodreadsLink
    },
    mutation: gql`
      mutation UpdatePenName (
        $id: Int!,
        $name: String,
        $bio: String,
        $image: String,
        $facebookLink: String,
        $bookbubLink: String,
        $twitterLink: String,
        $instagramLink: String,
        $websiteLink: String,
        $newsletterLink: String,
        $tiktokLink: String,
        $amazonLink: String,
        $goodreadsLink: String
      ) {
        updatePenName (
          id: $id,
          name: $name,
          bio: $bio,
          image: $image,
          facebookLink: $facebookLink,
          bookbubLink: $bookbubLink,
          twitterLink: $twitterLink,
          instagramLink: $instagramLink,
          websiteLink: $websiteLink,
          newsletterLink: $newsletterLink,
          tiktokLink: $tiktokLink,
          amazonLink: $amazonLink,
          goodreadsLink: $goodreadsLink
        ) {
          ...PenNameFragment
        }
      }
      ${penNameFragments.penName}
    `
  })
}

const checkDelete = (id: number): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query CheckDeletePenName ($id: Int!) {
        checkDeletePenName(id: $id) {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

const deletePenName = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation ConfirmDeletePenName (
        $id: Int!
      ) {
        confirmDeletePenName(id: $id) {
          id
        }
      }
    `
  })
}

const follow = (id: number): Promise<PenNameDto> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation FollowPenName (
        $id: Int!
      ) {
        followPenName(id: $id) {
          ...PenNameFragment
        }
      }
      ${penNameFragments.penName}
    `
  })
}

const unfollow = (id: number): Promise<PenNameDto> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation UnfollowPenName (
        $id: Int!
      ) {
        unfollowPenName(id: $id) {
          ...PenNameFragment
        }
      }
      ${penNameFragments.penName}
    `
  })
}

const getUrl = (model: PenNameDto): string => {
  return `https://booksprout.co/pen-name/${model.id}/${slugify(model.name)}`
}

/**
 * `globalLoader` is chained by the list pages, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  create,
  update,
  checkDelete,
  delete: deletePenName,
  follow,
  unfollow,
  getUrl,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function penNameService () {
  return service
}
